import { create } from 'zustand';

/**
 * Folder roles where an unread count carries no meaning: you don't "read" mail you sent
 * yourself, and unread state in the bin is noise. Matches how Gmail and Outlook present
 * these folders. Shared so the sidebar badge and the folder header never disagree.
 */
const ROLES_WITHOUT_UNREAD_COUNT = ['sent', 'trash'];

export function showsUnreadCount(role: string | undefined): boolean {
  return !ROLES_WITHOUT_UNREAD_COUNT.includes(role ?? '');
}

export interface Email {
  emailId: string;
  threadId: string;
  mailboxIds: string[];
  /** RFC 5322 Message-ID — needed to build In-Reply-To/References when replying. */
  messageId?: string;
  /** RFC 5322 References chain of this message. */
  references?: string[];
  from: { name?: string; email: string }[];
  to: { name?: string; email: string }[];
  cc: { name?: string; email: string }[];
  subject: string;
  preview: string;
  textBody?: string;
  htmlBody?: string;
  receivedAt: string;
  sentAt?: string;
  size: number;
  hasAttachment: boolean;
  keywords: Record<string, boolean>;
  pinned: boolean;
  attachments?: { name: string; blobId: string; type: string; size: number; contentId?: string; disposition?: string }[];
  security?: {
    spf: string;
    dkim: string;
    dmarc: string;
    spam: string;
    virus: string;
  };
  /** RFC 2156 / RFC 4021: Email priority — "highest", "high", "normal", "low", "lowest" */
  priority?: string;
  /** RFC 8098: Sender requested a read receipt */
  receiptRequested?: boolean;
  /** RFC 8098: Address to send the MDN to */
  dispositionNotificationTo?: string;
  /** Timestamp when a read receipt (MDN) was sent for this email */
  mdnSentAt?: string;
  /** Timestamp when a read receipt was received for this sent email */
  readReceiptAt?: string;
  /** Search snippet: highlighted preview from SearchSnippet/get */
  snippetSource?: string;
  /** Search snippet: highlighted subject from SearchSnippet/get */
  snippetSubject?: string;
  /** AI-6: Detected action items, deadlines, meetings */
  aiActions?: string;
  /** AI-7: Smart category (newsletter, receipt, calendar, automated, personal, announcement, action) */
  aiCategory?: string;
  /** RFC 2369: List-Unsubscribe header */
  listUnsubscribe?: string;
  /** Timestamp when user unsubscribed via AI-9 */
  unsubscribedAt?: string;
  /** AI-10: Inferred priority from content analysis (separate from RFC priority header) */
  aiPriority?: string;
  /** AI-14: Auto-cleanup action suggestion (archive, mark-read, or empty) */
  autoAction?: string;
  /** AI-14: Reason for the auto-cleanup action */
  autoActionReason?: string;
  /** External account ID if this email came from an external source */
  externalId?: string;
}

export interface Mailbox {
  id: string;
  name: string;
  role: string;
  parentId?: string;
  totalEmails: number;
  unreadEmails: number;
  color?: string;
  pinned: boolean;
  sortOrder: number;
}

export type SortField = 'date' | 'sender' | 'subject' | 'size' | 'relevance';
export type SortDirection = 'asc' | 'desc';
export type FilterType = 'all' | 'unread' | 'flagged' | 'attachment' | 'priority';

interface EmailState {
  mailboxes: Mailbox[];
  emails: Record<string, Email>;
  emailIds: string[];
  selectedMailboxId: string | null;
  selectedEmailId: string | null;
  isLoading: boolean;
  searchQuery: string;

  // Sorting & filtering
  sortField: SortField;
  sortDirection: SortDirection;
  filterType: FilterType;

  // Pagination
  pageSize: number;
  hasMore: boolean;
  queryPosition: number;

  setMailboxes: (mailboxes: Mailbox[]) => void;
  setEmails: (emails: Email[]) => void;
  setEmailIds: (ids: string[]) => void;
  appendEmailIds: (ids: string[]) => void;
  selectMailbox: (id: string) => void;
  selectEmail: (id: string | null) => void;
  setLoading: (loading: boolean) => void;
  setSearchQuery: (query: string) => void;
  setSortField: (field: SortField) => void;
  setSortDirection: (dir: SortDirection) => void;
  setFilterType: (filter: FilterType) => void;
  setHasMore: (hasMore: boolean) => void;
  setQueryPosition: (pos: number) => void;
  updateEmailKeywords: (emailId: string, keywords: Record<string, boolean>) => void;
  moveEmail: (emailId: string, mailboxId: string) => void;
  removeEmailFromList: (emailId: string) => void;
  decrementMailboxUnread: (mailboxId: string) => void;

  // Derived: sorted + filtered IDs
  getSortedFilteredIds: () => string[];
  // Thread helpers
  getThreadEmails: (threadId: string) => Email[];
  getThreadIds: () => string[]; // returns one emailId per thread (latest)
}

export const useEmailStore = create<EmailState>()((set, get) => ({
  mailboxes: [],
  emails: {},
  emailIds: [],
  selectedMailboxId: null,
  selectedEmailId: null,
  isLoading: false,
  searchQuery: '',
  sortField: 'date',
  sortDirection: 'desc',
  filterType: 'all',
  pageSize: 50,
  hasMore: false,
  queryPosition: 0,

  setMailboxes: (mailboxes) => set({ mailboxes }),

  setEmails: (emails) =>
    set((state) => {
      const map = { ...state.emails };
      for (const email of emails) {
        map[email.emailId] = email;
      }
      return { emails: map };
    }),

  setEmailIds: (ids) => set({ emailIds: ids, queryPosition: ids.length }),

  appendEmailIds: (ids) =>
    set((state) => ({
      emailIds: [...state.emailIds, ...ids],
      queryPosition: state.emailIds.length + ids.length,
    })),

  selectMailbox: (id) =>
    set({ selectedMailboxId: id, selectedEmailId: null, emailIds: [], queryPosition: 0, hasMore: false }),

  selectEmail: (id) => set({ selectedEmailId: id }),

  setLoading: (loading) => set({ isLoading: loading }),

  setSearchQuery: (query) => set({ searchQuery: query }),

  setSortField: (field) => set({ sortField: field }),

  setSortDirection: (dir) => set({ sortDirection: dir }),

  setFilterType: (filter) => {
    set({ filterType: filter });
    // Dispatch event so EmailModule re-queries the API with this filter
    window.dispatchEvent(new CustomEvent('prufe:filterChanged'));
  },

  setHasMore: (hasMore) => set({ hasMore }),

  setQueryPosition: (pos) => set({ queryPosition: pos }),

  updateEmailKeywords: (emailId, keywords) =>
    set((state) => {
      const email = state.emails[emailId];
      if (!email) return state;
      return {
        emails: {
          ...state.emails,
          [emailId]: { ...email, keywords: { ...email.keywords, ...keywords } },
        },
      };
    }),

  moveEmail: (emailId, mailboxId) =>
    set((state) => {
      const email = state.emails[emailId];
      if (!email) return state;
      return {
        emails: {
          ...state.emails,
          [emailId]: { ...email, mailboxIds: [mailboxId] },
        },
      };
    }),

  removeEmailFromList: (emailId) =>
    set((state) => ({
      emailIds: state.emailIds.filter((id) => id !== emailId),
    })),

  decrementMailboxUnread: (mailboxId) =>
    set((state) => ({
      mailboxes: state.mailboxes.map((m) =>
        m.id === mailboxId ? { ...m, unreadEmails: Math.max(0, m.unreadEmails - 1) } : m
      ),
    })),

  getSortedFilteredIds: () => {
    const state = get();
    let ids = [...state.emailIds];

    // Filter
    if (state.filterType !== 'all') {
      ids = ids.filter((id) => {
        const email = state.emails[id];
        if (!email) return false;
        switch (state.filterType) {
          case 'unread': return !email.keywords['$seen'];
          case 'flagged': return !!email.keywords['$flagged'];
          case 'attachment': return email.hasAttachment;
          case 'priority': return email.priority === 'high' || email.priority === 'highest' || email.aiPriority === 'high';
          default: return true;
        }
      });
    }

    // Sort — 'relevance' preserves the API-returned order (server-side scored)
    if (state.sortField !== 'relevance') {
      ids.sort((a, b) => {
        const ea = state.emails[a];
        const eb = state.emails[b];
        if (!ea || !eb) return 0;

        let cmp = 0;
        switch (state.sortField) {
          case 'date': {
            const da = new Date(ea.sentAt || ea.receivedAt).getTime();
            const db = new Date(eb.sentAt || eb.receivedAt).getTime();
            cmp = da - db;
            break;
          }
          case 'sender': {
            const sa = ea.from[0]?.name || ea.from[0]?.email || '';
            const sb = eb.from[0]?.name || eb.from[0]?.email || '';
            cmp = sa.localeCompare(sb);
            break;
          }
          case 'subject':
            cmp = (ea.subject || '').localeCompare(eb.subject || '');
            break;
          case 'size':
            cmp = ea.size - eb.size;
            break;
        }

        return state.sortDirection === 'desc' ? -cmp : cmp;
      });
    }

    return ids;
  },

  getThreadEmails: (threadId: string) => {
    const state = get();
    return Object.values(state.emails)
      .filter(e => e.threadId === threadId)
      .sort((a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime());
  },

  getThreadIds: () => {
    const state = get();
    // Group emails by threadId, return the latest emailId per thread
    const threadMap = new Map<string, { latestId: string; latestDate: number }>();

    for (const id of state.emailIds) {
      const email = state.emails[id];
      if (!email) continue;
      const tid = email.threadId || id;
      const date = new Date(email.receivedAt).getTime();
      const existing = threadMap.get(tid);
      if (!existing || date > existing.latestDate) {
        threadMap.set(tid, { latestId: id, latestDate: date });
      }
    }

    // Return the latest emailId per thread, sorted by date descending
    return [...threadMap.values()]
      .sort((a, b) => b.latestDate - a.latestDate)
      .map(t => t.latestId);
  },
}));
